const NON_DIGITS = /[^0-9]/g;
const NON_DECIMAL = /[^0-9.]/g;
const LEADING_DIGITS = /^\d+/;
const GROUP_SIZE = 3;

/**
 * Keeps only the characters the dimension accepts. Currency dimensions (AGI /
 * Pell) are whole dollars; the rest (GPA Raw, SAI) are decimals, so a single
 * dot survives and partially typed input like `3.` is preserved as-is.
 */
export const sanitizeEligibilityValue = (
  raw: string,
  isCurrency: boolean
): string => {
  if (isCurrency) {
    return raw.replace(NON_DIGITS, '');
  }

  const [whole, ...rest] = raw.replace(NON_DECIMAL, '').split('.');

  return rest.length ? `${whole}.${rest.join('')}` : whole;
};

/**
 * Inserts thousands separators in a single left-to-right pass. Deliberately not
 * the usual `\B(?=(\d{3})+(?!\d))` lookahead: that pattern backtracks
 * super-linearly over a long digit run, so a pasted value could stall the
 * render thread on every keystroke.
 */
const groupThousands = (digits: string): string => {
  const firstGroupLength = digits.length % GROUP_SIZE || GROUP_SIZE;
  const groups = [digits.slice(0, firstGroupLength)];

  for (let i = firstGroupLength; i < digits.length; i += GROUP_SIZE) {
    groups.push(digits.slice(i, i + GROUP_SIZE));
  }

  return groups.join(',');
};

/**
 * Display form of a stored value. Only the leading digit run is grouped, so a
 * decimal dimension's fractional digits are left alone.
 */
export const formatEligibilityValue = (
  value: string,
  isCurrency: boolean
): string =>
  isCurrency ? value.replace(LEADING_DIGITS, groupThousands) : value;
